// components/SchedulingEmployer/CreateScheduleSection.js
import React from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import {
  CalendarDays,
  Clock3,
  BriefcaseBusiness,
  CircleMinus,
  CircleDollarSign,
  Copy,
  Plus,
  Settings2,
} from "lucide-react";
import { flip, offset, shift as fuiShift, limitShift } from "@floating-ui/dom";
import "react-datepicker/dist/react-datepicker.css";

// Keep these styles consistent with your existing file
const SELECT_Z = 9999;
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: SELECT_Z }),
  menu: (base) => ({ ...base, zIndex: SELECT_Z }),
};

export default function CreateScheduleSection({
  employees,
  selectedEmployee,
  setSelectedEmployee,
  selectedEmployees,
  setSelectedEmployees,
  multiAssign,
  setMultiAssign,
  startDate,
  endDate,
  dateRange,
  setDateRange,
  repeatWeekly,
  setRepeatWeekly,
  shifts,
  setShifts, // ✅ add this
  handleShiftChange,
  addActivity,
  updateActivity,
  removeActivity,
  parseLocalDate,
  showConfirmModal,
  setShowConfirmModal,
  editingScheduleId,
  handleSaveSchedule,
  activityTypes,
  onCreateActivityType,
  onOpenTypesModal,
  toMin,
  isSaving,
  isTypesLoading,
  weeklyLeaveMap,
  locations,
  showWorksiteDropdown,
  primaryLocation,
}) {
  const hasPaidLeaveThisWeek = (empId) => {
    const days = weeklyLeaveMap?.[empId];
    if (!days) return false;
    return Object.values(days).some((d) => d.isPaid);
  };

  const makeId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}`;
  };

  const cloneActivities = (acts = []) =>
    (acts || []).map((a) => ({
      ...a,
      id: makeId(), // ✅ new id so rows don't share keys
    }));

  // Copy one day's settings to other days
  // mode: "rest" | "all"
  // Copy one day's settings to all other days in the selected week
  const applyDayToWeek = (sourceDateISO) => {
    const ordered = [...shifts]
      .filter((s) => !!s.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const sourceIndex = ordered.findIndex((s) => s.date === sourceDateISO);
    if (sourceIndex === -1) return;

    const source = ordered[sourceIndex];

    // targets = every other day in the week
    const targets = ordered.filter((s) => s.date !== sourceDateISO);

    if (!targets.length) {
      toast.info("No other days to apply to.");
      return;
    }

    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);

    // ✅ Find the first day in the week that can be used as the "copy source"

    // ✅ summary object (no += side effects leaking across StrictMode runs)
    let summary = { applied: 0, skippedPast: 0, skippedLeave: 0 };

    setShifts((prev) => {
      let applied = 0;
      let skippedPast = 0;
      let skippedLeave = 0;

      const next = prev.map((s) => {
        if (!s?.date) return s;

        const isTarget = targets.some((t) => t.date === s.date);
        if (!isTarget) return s;

        const dayDate = parseLocalDate(s.date);
        dayDate.setHours(0, 0, 0, 0);
        const isPastDay = dayDate < today0;

        const leaveInfo =
          !multiAssign && selectedEmployee
            ? weeklyLeaveMap?.[selectedEmployee]?.[s.date]
            : null;

        const isOnLeave = !!leaveInfo;

        if (isPastDay) {
          skippedPast += 1;
          return s;
        }
        if (isOnLeave) {
          skippedLeave += 1;
          return s;
        }

        applied += 1;

        return {
          ...s,
          startTime: source.startTime,
          stopTime: source.stopTime,
          isOffDay: !!source.isOffDay,
          isHoliday: !!source.isHoliday,
          isCallOut: !!source.isCallOut, // ✅ ADD
          locationId: source.locationId || null,
          locationName: source.locationName || "",
          activities: cloneActivities(source.activities || []),
        };
      });

      // ✅ overwrite summary (no double counting)
      summary = { applied, skippedPast, skippedLeave };

      return next;
    });

    setTimeout(() => {
      if (summary.applied)
        toast.success(`Applied to ${summary.applied} day(s).`);
      if (summary.skippedPast)
        toast.warn(`Skipped ${summary.skippedPast} past day(s).`);
      if (summary.skippedLeave)
        toast.warn(`Skipped ${summary.skippedLeave} approved leave day(s).`);
    }, 0);
  };

  const getHourlyRate = (emp) => {
    if (!emp) return 0;
    const raw =
      emp.hourlyRate ??
      emp.hourly_rate ??
      emp.payRate ??
      emp.pay_rate ??
      emp.rate ??
      0;

    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const formatMoney = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(n || 0));

  // ✅ full-day leave detection (half-day still counts as “working”)
  const isHalfDayLeave = (leaveInfo) => {
    if (!leaveInfo) return false;
    const tod = String(leaveInfo.timeOfDay || "").toLowerCase();
    return (
      tod.includes("half") ||
      tod.includes("am") ||
      tod.includes("pm") ||
      !!(leaveInfo.customStart && leaveInfo.customEnd)
    );
  };

  const isFullDayLeave = (leaveInfo) =>
    !!leaveInfo && !isHalfDayLeave(leaveInfo);

  const getShiftBreakdown = (shift) => {
    if (!shift || shift.isOffDay || shift.isHoliday || shift.isCallOut) {
      return { baseMin: 0, paidMin: 0, unpaidMin: 0, totalMin: 0 };
    }

    const sMin = toMin(shift.startTime);
    const eMinRaw = toMin(shift.stopTime);

    // Shift end adjusted for overnight shifts
    const shiftStart = sMin;
    const shiftEnd = eMinRaw > sMin ? eMinRaw : eMinRaw + 1440;

    const baseMin = Math.max(0, shiftEnd - shiftStart);

    let paidMin = 0; // ✅ paid minutes OUTSIDE the base window only
    let unpaidMin = 0; // ✅ unpaid minutes INSIDE the base window only

    const acts = shift.activities || [];

    // helper: compute overlap between [aStart,aEnd] and [shiftStart,shiftEnd]
    const overlapMin = (aStart, aEnd) =>
      Math.max(0, Math.min(aEnd, shiftEnd) - Math.max(aStart, shiftStart));

    for (const a of acts) {
      if (!a?.start || !a?.end) continue;

      let aStart = toMin(a.start);
      let aEnd = toMin(a.end);

      // handle activity overnight span
      if (aEnd <= aStart) aEnd += 1440;

      // ✅ align activities to the same "day range" as the shift window
      // If shift is overnight (shiftEnd > 1440), an activity after midnight
      // might need to be moved forward by +1440 to match overlap correctly.
      if (shiftEnd > 1440) {
        // if activity ends before shiftStart in raw minutes, it's probably next-day
        if (aEnd <= shiftStart) {
          aStart += 1440;
          aEnd += 1440;
        }
      }

      const dur = Math.max(0, aEnd - aStart);
      if (dur <= 0) continue;

      const inside = overlapMin(aStart, aEnd); // portion inside shift window
      const outside = Math.max(0, dur - inside); // portion outside shift window

      if (a.paid) {
        // ✅ paid time inside the shift is already covered by base window
        // Only count paid minutes that happen OUTSIDE the shift window
        paidMin += outside;
      } else {
        // ✅ unpaid time only matters if it overlaps the base window (break)
        unpaidMin += inside;
      }
    }

    const totalMin = Math.max(0, baseMin + paidMin - unpaidMin);
    return { baseMin, paidMin, unpaidMin, totalMin };
  };

  const perDay = shifts
    .filter((s) => !!s.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((s) => ({ date: s.date, day: s.day, ...getShiftBreakdown(s) }));

  const weekTotals = perDay.reduce(
    (acc, d) => {
      acc.baseMin += d.baseMin;
      acc.paidMin += d.paidMin;
      acc.unpaidMin += d.unpaidMin;
      acc.totalMin += d.totalMin;
      return acc;
    },
    { baseMin: 0, paidMin: 0, unpaidMin: 0, totalMin: 0 },
  );

  const scheduledHours = weekTotals.totalMin / 60;

  const scheduledEarnings = (() => {
    if (multiAssign) {
      const selected = employees.filter((e) =>
        selectedEmployees.includes(e.id),
      );
      return selected.reduce(
        (sum, emp) => sum + scheduledHours * getHourlyRate(emp),
        0,
      );
    }
    const emp = employees.find((e) => e.id === selectedEmployee);
    return scheduledHours * getHourlyRate(emp);
  })();

  const mmToHours = (m) => (m / 60).toFixed(2);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const copySourceDate = (() => {
    const ordered = [...shifts]
      .filter((s) => !!s.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    for (const s of ordered) {
      const dayDate = parseLocalDate(s.date);
      dayDate.setHours(0, 0, 0, 0);
      const isPastDay = dayDate < today;

      const leaveInfo =
        !multiAssign && selectedEmployee
          ? weeklyLeaveMap?.[selectedEmployee]?.[s.date] || null
          : null;

      const fullDayLeave = isFullDayLeave(leaveInfo);

      const isEligible =
        !isPastDay &&
        !fullDayLeave &&
        !s.isOffDay &&
        !s.isHoliday &&
        !s.isCallOut;

      if (isEligible) return s.date;
    }

    return null; // no eligible day this week
  })();

  return (
    <div className="schedule-card-container create-schedule-form">
      {/*

      <div className="toggle-row" style={{ marginBottom: 8 }}>
        <span>Multi-assign</span>
        <label className="switchs">
          <input
            type="checkbox"
            checked={multiAssign}
            onChange={(e) => {
              setMultiAssign(e.target.checked);
              if (e.target.checked) setSelectedEmployee("");
              else setSelectedEmployees([]);
            }}
            disabled={!!editingScheduleId}
          />
          <span className="slider round"></span>
        </label>
      </div>
*/}
      <div className="create-schedule-top-grid">
        <div className="create-schedule-field">
          <div className="create-schedule-field-label">Employee</div>
          {multiAssign ? (
            <>
              {(() => {
                const options = employees
                  .filter((e) => e.status === "Active")
                  .map((emp) => {
                    const blocked = hasPaidLeaveThisWeek(emp.id);
                    return {
                      value: emp.id,
                      label: `${emp.name} (${emp.role || "Employee"})${
                        blocked ? " — on paid leave this week" : ""
                      }`,
                      isDisabled: blocked,
                    };
                  });

                const currentValues = selectedEmployees
                  .map((id) => options.find((o) => o.value === id) || null)
                  .filter(Boolean);

                return (
                  <>
                    <Select
                      isMulti
                      options={options}
                      value={currentValues}
                      isOptionDisabled={(opt) => opt.isDisabled}
                      onChange={(opts) => {
                        const picked = (opts || []).filter(
                          (o) => !o.isDisabled,
                        );
                        const blocked = (opts || []).filter(
                          (o) => o.isDisabled,
                        );

                        setSelectedEmployees(picked.map((o) => o.value));

                        if (blocked.length) {
                          toast.warn(
                            `${
                              blocked.length === 1
                                ? "1 employee is"
                                : `${blocked.length} employees are`
                            } on approved paid leave for this week and can't be assigned this schedule.`,
                          );
                        }
                      }}
                      placeholder="Select one or more employees…"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      styles={selectStyles}
                    />
                    <div
                      style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}
                    >
                      Selected: {selectedEmployees.length}
                    </div>
                  </>
                );
              })()}
            </>
          ) : (
            <Select
              options={employees
                .filter((e) => e.status === "Active")
                .map((emp) => ({
                  value: emp.id,
                  label: `${emp.name} (${emp.role || "Employee"})`,
                }))}
              onChange={(opt) => setSelectedEmployee(opt?.value || "")}
              placeholder="Select an employee"
              value={
                selectedEmployee
                  ? {
                      value: selectedEmployee,
                      label:
                        employees.find((e) => e.id === selectedEmployee)
                          ?.name || "",
                    }
                  : null
              }
              isClearable
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
              styles={selectStyles}
            />
          )}
        </div>

        <div className="create-schedule-field">
          <div className="custom-date-wrapper">
            <div className="week-field-heading">
              <div className="create-schedule-field-label">Select Week</div>

              <div className="create-schedule-field-help">
                Choose the start date for the week. The schedule will
                automatically cover 7 days.
              </div>
            </div>

            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                const [start, end] = update || [];
                if (start && !end) {
                  const seven = new Date(start);
                  seven.setDate(seven.getDate() + 6);
                  setDateRange([start, seven]);
                  return;
                }
                if (start && end) {
                  const days = (end - start) / (1000 * 60 * 60 * 24) + 1;
                  if (days !== 7) {
                    toast.error(
                      "Please select exactly one full week (7 days).",
                    );
                    return;
                  }
                }
                setDateRange(update);
              }}
              placeholderText="Select start and end dates"
              popperProps={{
                placement: "bottom-start",
                strategy: "fixed",
                modifiers: [
                  offset(8),
                  flip({ fallbackPlacements: [] }),
                  fuiShift({ limiter: limitShift(), padding: 8 }),
                ],
              }}
            />

            {(startDate || endDate) && (
              <button
                type="button"
                className="clear-date-btn"
                onClick={() => setDateRange([null, null])}
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {startDate && endDate && (
        <div className="create-selected-week">
          <div className="create-selected-week-icon">
            <CalendarDays size={19} />
          </div>

          <div>
            <span>Selected Week: </span>
            <strong>{startDate.toDateString()}</strong>
            <span> – </span>
            <strong>{endDate.toDateString()}</strong>
          </div>
        </div>
      )}
      <div className="schedule-toggle-row create-repeat-row">
        <div className="create-repeat-label">
          <span className="create-repeat-icon">↻</span>
          <span>Repeat this schedule weekly</span>
        </div>

        <label className="schedule-switchs">
          <input
            type="checkbox"
            checked={repeatWeekly}
            onChange={(e) => setRepeatWeekly(e.target.checked)}
          />
          <span className="schedule-slider round"></span>
        </label>
      </div>

      <div className="wo-kpis create-kpis">
        <div className="kpi create-kpi create-kpi-hours">
          <div className="create-kpi-icon">
            <Clock3 size={21} />
          </div>

          <div>
            <div className="kpi-label">Total shift hours</div>
            <div className="kpi-value">{mmToHours(weekTotals.baseMin)} hrs</div>
          </div>
        </div>

        <div className="kpi create-kpi create-kpi-paid">
          <div className="create-kpi-icon">
            <BriefcaseBusiness size={21} />
          </div>

          <div>
            <div className="kpi-label">Paid activities</div>
            <div className="kpi-value">
              +{mmToHours(weekTotals.paidMin)} hrs
            </div>
          </div>
        </div>

        <div className="kpi create-kpi create-kpi-unpaid">
          <div className="create-kpi-icon">
            <CircleMinus size={21} />
          </div>

          <div>
            <div className="kpi-label">Unpaid activities</div>
            <div className="kpi-value">
              -{mmToHours(weekTotals.unpaidMin)} hrs
            </div>
          </div>
        </div>

        <div className="kpi create-kpi create-kpi-total">
          <div className="create-kpi-icon">
            <Clock3 size={21} />
          </div>

          <div>
            <div className="kpi-label">Paid hours (week total)</div>
            <div className="kpi-value">
              {mmToHours(weekTotals.totalMin)} hrs
            </div>
          </div>
        </div>

        <div className="kpi create-kpi create-kpi-money">
          <div className="create-kpi-icon">
            <CircleDollarSign size={22} />
          </div>

          <div>
            <div className="kpi-label">Scheduled earnings</div>
            <div className="kpi-value">{formatMoney(scheduledEarnings)}</div>
          </div>
        </div>
      </div>

      {/* ✅ REDESIGNED WEEK EDITOR (DAY CARDS) */}
      {startDate && endDate && (
        <div className="week-cards">
          {[...shifts]
            .filter((s) => !!s.date)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map((shift, idx) => {
              const dayDate = parseLocalDate(shift.date);
              dayDate.setHours(0, 0, 0, 0);
              const isPastDay = dayDate < today;

              const dateISO = shift.date;
              const leaveInfo =
                !multiAssign && selectedEmployee
                  ? weeklyLeaveMap?.[selectedEmployee]?.[dateISO]
                  : null;
              const isOnLeave = !!leaveInfo;
              const leaveLabel =
                isOnLeave && leaveInfo.isPaid
                  ? "Paid leave (approved)"
                  : isOnLeave
                  ? "Unpaid leave (approved)"
                  : null;

              const breakdown = getShiftBreakdown(shift);

              const weekday = parseLocalDate(
                shift.date,
              ).toLocaleDateString("en-US", { weekday: "long" });
              const dateLabel = parseLocalDate(shift.date).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                },
              );

              return (
                <div
                  key={shift.date}
                  className={`day-card ${isPastDay ? "is-past" : ""} ${
                    isOnLeave ? "is-leave" : ""
                  }`}
                >
                  {/* HEADER */}
                  <div className="day-card__header">
                    <div className="day-card__headerLeft">
                      <div className="day-date-badge">
                        <span className="day-date-badge__dow">
                          {weekday.slice(0, 3).toUpperCase()}
                        </span>

                        <strong className="day-date-badge__day">
                          {parseLocalDate(shift.date).getDate()}
                        </strong>

                        <span className="day-date-badge__month">
                          {parseLocalDate(shift.date)
                            .toLocaleDateString("en-US", { month: "short" })
                            .toUpperCase()}
                        </span>
                      </div>
                      <div className="day-card__title">
                        <div className="day-card__weekday">{weekday}</div>
                        <div className="day-card__date">{dateLabel}</div>
                      </div>

                      <div className="day-card__chips">
                        {isPastDay && (
                          <span className="chip chip--past">Past</span>
                        )}
                        {isOnLeave && (
                          <span className="chip chip--leave">
                            {leaveLabel || "Approved leave"}
                          </span>
                        )}

                        <button
                          type="button"
                          className={`chip-btn ${
                            shift.isOffDay ? "is-on" : ""
                          }`}
                          onClick={() => {
                            const next = !shift.isOffDay;
                            if (next)
                              handleShiftChange(shift.day, "isCallOut", false); // ✅ ADD
                            handleShiftChange(shift.day, "isOffDay", next);
                          }}
                          disabled={isPastDay || isOnLeave}
                        >
                          Off Day
                        </button>

                        <button
                          type="button"
                          className={`chip-btn ${
                            shift.isHoliday ? "is-on" : ""
                          }`}
                          onClick={() => {
                            const next = !shift.isHoliday;
                            if (next)
                              handleShiftChange(shift.day, "isCallOut", false); // ✅ ADD
                            handleShiftChange(shift.day, "isHoliday", next);
                          }}
                          disabled={isPastDay || isOnLeave}
                        >
                          Holiday
                        </button>
                        <button
                          type="button"
                          className={`chip-btn ${
                            shift.isCallOut ? "is-on" : ""
                          }`}
                          onClick={() => {
                            const next = !shift.isCallOut;

                            // ✅ Turn ON Call-out:
                            // - ensure it can't conflict with Off Day / Holiday
                            // - keep the shift time stored (for audit), but it won't count in totals
                            if (next) {
                              handleShiftChange(shift.day, "isOffDay", false);
                              handleShiftChange(shift.day, "isHoliday", false);
                            }

                            handleShiftChange(shift.day, "isCallOut", next);
                          }}
                          disabled={isPastDay || isOnLeave}
                          title="Mark as call-out (excused absence for a scheduled work day)"
                        >
                          Call-out
                        </button>
                      </div>
                    </div>
                    {!shift.isOffDay && !shift.isHoliday && !shift.isCallOut ? (
                      <div className="day-card__total">
                        <div className="day-card__totalMain">
                          Scheduled:{" "}
                          <strong>{mmToHours(breakdown.totalMin)} hrs</strong>
                        </div>
                        <div className="day-card__totalSub">
                          Base {mmToHours(breakdown.baseMin)}h • +Paid{" "}
                          {mmToHours(breakdown.paidMin)}h • -Unpaid{" "}
                          {mmToHours(breakdown.unpaidMin)}h
                        </div>
                      </div>
                    ) : (
                      <div className="day-card__total day-card__total--muted">
                        <div className="day-card__totalMain">
                          {shift.isOffDay
                            ? "Off Day"
                            : shift.isHoliday
                            ? "Holiday"
                            : "Call-out"}
                        </div>
                        <div className="day-card__totalSub">
                          {shift.isCallOut
                            ? "Excused absence — no shift hours counted"
                            : "No shift hours for this day"}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BODY */}
                  {!shift.isOffDay && !shift.isHoliday && !shift.isCallOut && (
                    <div className="day-card__body">
                      {shift.date === copySourceDate && (
                        <button
                          type="button"
                          className="apply-week-btn"
                          onClick={() => applyDayToWeek(shift.date)}
                          disabled={isPastDay || isOnLeave}
                          title="Copy this day’s time, worksite, and activities to the other days in this week"
                        >
                          <Copy size={16} />
                          <span>Copy to Other Days</span>
                        </button>
                      )}

                      {/* LEFT: SHIFT BASICS */}
                      <div className="day-card__left">
                        {/* Worksite */}
                        <div className="field">
                          <div className="field__label">Worksite</div>

                          {showWorksiteDropdown ? (
                            <Select
                              options={(locations || []).map((l) => ({
                                value: l.id,
                                label:
                                  l.name ||
                                  l.locationName ||
                                  "Unnamed location",
                              }))}
                              value={
                                shift.locationId
                                  ? {
                                      value: shift.locationId,
                                      label:
                                        shift.locationName ||
                                        (locations || []).find(
                                          (l) => l.id === shift.locationId,
                                        )?.name ||
                                        (locations || []).find(
                                          (l) => l.id === shift.locationId,
                                        )?.locationName ||
                                        "Worksite",
                                    }
                                  : null
                              }
                              onChange={(opt) => {
                                handleShiftChange(
                                  shift.day,
                                  "locationId",
                                  opt?.value || null,
                                );
                                const loc = (locations || []).find(
                                  (l) => l.id === opt?.value,
                                );
                                handleShiftChange(
                                  shift.day,
                                  "locationName",
                                  loc?.name || loc?.locationName || "",
                                );
                              }}
                              isClearable
                              placeholder="Select a worksite…"
                              menuPortalTarget={
                                typeof document !== "undefined"
                                  ? document.body
                                  : null
                              }
                              menuPosition="fixed"
                              menuShouldScrollIntoView={false}
                              styles={selectStyles}
                              isDisabled={isPastDay || isOnLeave}
                            />
                          ) : (
                            <div className="static-field">
                              {primaryLocation?.name || "Main Office"}
                            </div>
                          )}
                        </div>

                        {/* Shift time */}
                        <div className="field">
                          <div className="field__label">Shift time</div>
                          <div className="time-row">
                            <input
                              type="time"
                              value={shift.startTime}
                              onChange={(e) =>
                                handleShiftChange(
                                  shift.day,
                                  "startTime",
                                  e.target.value,
                                )
                              }
                              disabled={isPastDay || isOnLeave}
                            />
                            <span className="time-row__to">to</span>
                            <input
                              type="time"
                              value={shift.stopTime}
                              onChange={(e) =>
                                handleShiftChange(
                                  shift.day,
                                  "stopTime",
                                  e.target.value,
                                )
                              }
                              disabled={isPastDay || isOnLeave}
                            />
                          </div>
                        </div>
                      </div>

                      {/* RIGHT: ACTIVITIES */}
                      <div className="day-card__right">
                        <div className="activities-editor">
                          <div className="field__label">Activities</div>
                          {(shift.activities || []).map((a) => (
                            <div className="activity-row" key={a.id}>
                              <div style={{ minWidth: 180, flex: "0 0 240px" }}>
                                <CreatableSelect
                                  isDisabled={
                                    isPastDay || isTypesLoading || isOnLeave
                                  }
                                  formatCreateLabel={(input) =>
                                    `Create "${input}"`
                                  }
                                  formatOptionLabel={(opt) => {
                                    const t = activityTypes.find(
                                      (x) => x.id === opt.value,
                                    );
                                    const color = t?.color;
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 3,
                                            background: color || "#d1d5db",
                                            boxShadow:
                                              "inset 0 0 0 1px rgba(0,0,0,.15)",
                                          }}
                                        />
                                        <span>{opt.label}</span>
                                      </div>
                                    );
                                  }}
                                  placeholder="Activity type…"
                                  value={
                                    a.typeId
                                      ? { value: a.typeId, label: a.type }
                                      : a.type
                                      ? { value: null, label: a.type }
                                      : null
                                  }
                                  onChange={(opt) => {
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "type",
                                      opt?.label || "",
                                    );
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "typeId",
                                      opt?.value || null,
                                    );
                                    const t = activityTypes.find(
                                      (t) => t.id === opt?.value,
                                    );
                                    if (t)
                                      updateActivity(
                                        shift.day,
                                        a.id,
                                        "paid",
                                        !!t.paidDefault,
                                      );
                                  }}
                                  onCreateOption={async (input) => {
                                    const t = await onCreateActivityType(input);
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "type",
                                      t.label,
                                    );
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "typeId",
                                      t.id,
                                    );
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "paid",
                                      !!t.paidDefault,
                                    );
                                  }}
                                  options={activityTypes.map((t) => ({
                                    value: t.id,
                                    label: t.label,
                                  }))}
                                  menuPortalTarget={
                                    typeof document !== "undefined"
                                      ? document.body
                                      : null
                                  }
                                  menuPosition="fixed"
                                  menuShouldScrollIntoView={false}
                                  styles={selectStyles}
                                />
                              </div>

                              <input
                                type="time"
                                value={a.start}
                                onChange={(e) =>
                                  updateActivity(
                                    shift.day,
                                    a.id,
                                    "start",
                                    e.target.value,
                                  )
                                }
                                disabled={isPastDay || isOnLeave}
                              />
                              <span>to</span>
                              <input
                                type="time"
                                value={a.end}
                                onChange={(e) =>
                                  updateActivity(
                                    shift.day,
                                    a.id,
                                    "end",
                                    e.target.value,
                                  )
                                }
                                disabled={isPastDay || isOnLeave}
                              />

                              <label className="paid-check">
                                <input
                                  className="paid-checkbox"
                                  type="checkbox"
                                  checked={!!a.paid}
                                  onChange={(e) =>
                                    updateActivity(
                                      shift.day,
                                      a.id,
                                      "paid",
                                      e.target.checked,
                                    )
                                  }
                                  disabled={isPastDay || isOnLeave}
                                />
                                Paid
                              </label>

                              <input
                                type="text"
                                placeholder="Note (optional)"
                                value={a.note || ""}
                                onChange={(e) =>
                                  updateActivity(
                                    shift.day,
                                    a.id,
                                    "note",
                                    e.target.value,
                                  )
                                }
                                style={{ flex: 1, minWidth: 140 }}
                                disabled={isPastDay || isOnLeave}
                              />

                              <button
                                className="general-deletebutton"
                                type="button"
                                onClick={() => removeActivity(shift.day, a.id)}
                                disabled={isPastDay || isOnLeave}
                              >
                                Delete
                              </button>
                            </div>
                          ))}

                          <div className="activity-actions">
                            <button
                              type="button"
                              className="add-activity"
                              onClick={() => addActivity(shift.day)}
                              disabled={isPastDay || isOnLeave}
                            >
                              <Plus size={17} />
                              <span>Add activity</span>
                            </button>

                            <button
                              type="button"
                              className="new-type-btn"
                              onClick={() => onOpenTypesModal?.()}
                              disabled={
                                isPastDay || isTypesLoading || isOnLeave
                              }
                              title={
                                isTypesLoading ? "Loading types…" : undefined
                              }
                            >
                              <Settings2 size={17} />

                              <span>
                                {isTypesLoading
                                  ? "Loading types…"
                                  : "Manage activity types"}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {showConfirmModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Confirm Schedule</h3>
            <p>
              {editingScheduleId
                ? "Confirm updating schedule for selected employee?"
                : "Confirm creation of schedule for selected employee?"}
            </p>
            <div className="modal-actions">
              <button
                className="general-savebuttons"
                type="button"
                onClick={handleSaveSchedule}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Confirm"}
              </button>
              <button
                className="general-cancelbuttons"
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
